import { IngredientInterestGroup } from '@/utils/ingredientInterestGroups';
import type {
  PortfolioFilterCapabilities,
  PortfolioFilterPolicy,
} from '@/utils/portfolioInterestModes';
import { AnyAction, Effect } from '@umijs/max';
import { message } from 'antd';
import {
  addBrandsOfInterest,
  addExcludedManufacturers,
  addExcludedMarketers,
  addManufacturersOfInterest,
  addMarketersOfInterest,
  changePassword,
  clearBrandsOfInterest,
  clearManufacturersOfInterest,
  clearMarketersOfInterest,
  createIngredientInterestGroup,
  deleteIngredientInterestGroup,
  fetchBrandsOfInterest,
  fetchCurrentPlan,
  fetchCurrentUser,
  fetchExcludedManufacturers,
  fetchExcludedMarketers,
  fetchIngredientInterestGroups,
  fetchIngredientsOfInterest,
  fetchIngredientsToReplace,
  fetchManufacturersOfInterest,
  fetchMarketersOfInterest,
  fetchPortfolioProductInterests,
  fetchPortfolioScopeInterests,
  removeBrandOfInterest,
  removeExcludedManufacturers,
  removeExcludedMarketers,
  removeIngredientsOfInterest,
  removeIngredientsToReplace,
  removeManufacturerOfInterest,
  removeMarketerOfInterest,
  updateIngredientInterestGroup,
  updateIngredientsOfInterest,
  updateIngredientsToReplace,
  updatePortfolioProductInterests,
  updatePortfolioScopeInterests,
  updateUserProfile,
} from './service';

export interface UserData {
  id: number;
  fullname: string;
  email: string;
  phone_number?: string;
  role?: 'admin' | 'user' | 'people_only';
  user_type?: 'ingredients' | 'packaging';
  feature_flags?: Record<string, boolean>;
  client?: {
    id?: number;
    name?: string;
    slug?: string;
    portfolio_interest_labels?: {
      marketers?: string;
      manufacturers?: string;
    };
  } | null;
  query_filter_mode?: 'simple' | 'composite' | 'both';
  ingredient_interest_mode?: 'portfolio' | 'grouped';
  portfolio_filter_policy?: PortfolioFilterPolicy;
  portfolio_filter_capabilities?: PortfolioFilterCapabilities;
}

export interface CreditsData {
  subscription: {
    id: number;
    name: string;
    description: string;
    price?: number;
    people_credits: number;
    product_credits: number;
    duration?: number;
    duration_name?: string;
  };
  status?: string;
  people_credits_remaining: number;
  product_credits_remaining: number;
  next_renewal_date: string;
  people_credit_costs?: {
    email: number;
    phone: number;
  };
}

export interface MarketerData {
  id: number;
  name: string;
  email?: string;
}

export interface ManufacturerData {
  id: number;
  name: string;
  email?: string;
}

export interface BrandData {
  id: number;
  name: string;
}

export type IngredientData = Record<string, unknown> & {
  id?: number | string;
  name?: string;
};

export interface PortfolioScopeInterestsData {
  applications: Array<{
    id: number;
    name: string;
    constraints?: {
      country_of_origin?: { include?: string[]; exclude?: string[] };
    };
  }>;
  countries_of_origin: string[];
  countries_of_sale: string[];
  access_constraints: {
    country_of_origin?: { include?: string[]; exclude?: string[] };
  };
}

export interface PortfolioProductInterestData {
  id: number;
  name: string;
}

export interface PortfolioProductInterestsData {
  flavours: PortfolioProductInterestData[];
  fragrances: PortfolioProductInterestData[];
  packaging_types: PortfolioProductInterestData[];
  packaging_materials: PortfolioProductInterestData[];
}

interface InterestListsLoaded {
  marketers: boolean;
  manufacturers: boolean;
  brands: boolean;
  ingredients: boolean;
  ingredientGroups: boolean;
  replacementIngredients: boolean;
  portfolioScope: boolean;
  productAttributes: boolean;
}

const DEFAULT_INTEREST_LISTS_LOADED: InterestListsLoaded = {
  marketers: false,
  manufacturers: false,
  brands: false,
  ingredients: false,
  ingredientGroups: false,
  replacementIngredients: false,
  portfolioScope: false,
  productAttributes: false,
};

export interface ProfileState {
  userData: UserData | null;
  creditsData: CreditsData | null;
  excludedMarketers: MarketerData[];
  excludedManufacturers: ManufacturerData[];
  marketersOfInterest: MarketerData[];
  manufacturersOfInterest: ManufacturerData[];
  brandsOfInterest: BrandData[];
  ingredientsOfInterest: IngredientData[];
  ingredientInterestGroups: IngredientInterestGroup[];
  ingredientsToReplace: IngredientData[];
  portfolioScopeInterests: PortfolioScopeInterestsData;
  flavoursOfInterest: PortfolioProductInterestData[];
  fragrancesOfInterest: PortfolioProductInterestData[];
  packagingTypesOfInterest: PortfolioProductInterestData[];
  packagingMaterialsOfInterest: PortfolioProductInterestData[];
  interestListsLoaded: InterestListsLoaded;
  loading: boolean;
}

export interface ProfileModelType {
  namespace: 'profile';
  state: ProfileState;
  effects: {
    fetchUserData: Effect;
    fetchCreditsData: Effect;
    updateProfile: Effect;
    changePassword: Effect;
    fetchExcludedMarketers: Effect;
    addExcludedMarketers: Effect;
    removeExcludedMarketers: Effect;
    fetchExcludedManufacturers: Effect;
    addExcludedManufacturers: Effect;
    removeExcludedManufacturers: Effect;
    fetchMarketersOfInterest: Effect;
    addMarketersOfInterest: Effect;
    removeMarketerOfInterest: Effect;
    clearMarketersOfInterest: Effect;
    fetchManufacturersOfInterest: Effect;
    addManufacturersOfInterest: Effect;
    removeManufacturerOfInterest: Effect;
    clearManufacturersOfInterest: Effect;
    fetchBrandsOfInterest: Effect;
    addBrandsOfInterest: Effect;
    removeBrandOfInterest: Effect;
    clearBrandsOfInterest: Effect;
    fetchIngredientsOfInterest: Effect;
    fetchIngredientInterestGroups: Effect;
    createIngredientInterestGroup: Effect;
    updateIngredientInterestGroup: Effect;
    deleteIngredientInterestGroup: Effect;
    updateIngredientsOfInterest: Effect;
    removeIngredientsOfInterest: Effect;
    clearIngredientsOfInterest: Effect;
    fetchIngredientsToReplace: Effect;
    updateIngredientsToReplace: Effect;
    removeIngredientsToReplace: Effect;
    clearIngredientsToReplace: Effect;
    fetchPortfolioScopeInterests: Effect;
    updatePortfolioScopeInterests: Effect;
    fetchPortfolioProductInterests: Effect;
    updatePortfolioProductInterests: Effect;
  };
  reducers: {
    setUserData: (state: ProfileState, action: { payload: UserData | null }) => ProfileState;
    setCreditsData: (state: ProfileState, action: { payload: CreditsData | null }) => ProfileState;
    setExcludedMarketers: (
      state: ProfileState,
      action: { payload: MarketerData[] },
    ) => ProfileState;
    setExcludedManufacturers: (
      state: ProfileState,
      action: { payload: ManufacturerData[] },
    ) => ProfileState;
    setMarketersOfInterest: (
      state: ProfileState,
      action: { payload: MarketerData[] },
    ) => ProfileState;
    setManufacturersOfInterest: (
      state: ProfileState,
      action: { payload: ManufacturerData[] },
    ) => ProfileState;
    setBrandsOfInterest: (state: ProfileState, action: { payload: BrandData[] }) => ProfileState;
    setIngredientsOfInterest: (
      state: ProfileState,
      action: { payload: IngredientData[] },
    ) => ProfileState;
    setIngredientInterestGroups: (
      state: ProfileState,
      action: { payload: IngredientInterestGroup[] },
    ) => ProfileState;
    setIngredientsToReplace: (
      state: ProfileState,
      action: { payload: IngredientData[] },
    ) => ProfileState;
    setPortfolioScopeInterests: (
      state: ProfileState,
      action: { payload: PortfolioScopeInterestsData },
    ) => ProfileState;
    setPortfolioProductInterests: (
      state: ProfileState,
      action: { payload: PortfolioProductInterestsData },
    ) => ProfileState;
    resetInterestLists: (state: ProfileState) => ProfileState;
    setLoading: (state: ProfileState, action: { payload: boolean }) => ProfileState;
  };
}

const DEFAULT_PORTFOLIO_SCOPE_INTERESTS: PortfolioScopeInterestsData = {
  applications: [],
  countries_of_origin: [],
  countries_of_sale: [],
  access_constraints: {},
};

const getIngredientsFromResponse = (response: any): IngredientData[] => {
  const rows = Array.isArray(response?.ingredients)
    ? response.ingredients
    : Array.isArray(response)
    ? response
    : [];

  return rows.filter(
    (item: unknown): item is IngredientData =>
      Boolean(item) && typeof item === 'object' && !Array.isArray(item),
  );
};

const ProfileModel: ProfileModelType = {
  namespace: 'profile',
  state: {
    userData: null,
    creditsData: null,
    excludedMarketers: [],
    excludedManufacturers: [],
    marketersOfInterest: [],
    manufacturersOfInterest: [],
    brandsOfInterest: [],
    ingredientsOfInterest: [],
    ingredientInterestGroups: [],
    ingredientsToReplace: [],
    portfolioScopeInterests: { ...DEFAULT_PORTFOLIO_SCOPE_INTERESTS },
    flavoursOfInterest: [],
    fragrancesOfInterest: [],
    packagingTypesOfInterest: [],
    packagingMaterialsOfInterest: [],
    interestListsLoaded: { ...DEFAULT_INTEREST_LISTS_LOADED },
    loading: false,
  },
  effects: {
    *fetchUserData(_, { call, put }): Generator<any, void, any> {
      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchCurrentUser);
        yield put({
          type: 'setUserData',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
        message.error('Failed to load user profile data');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *fetchCreditsData(_, { call, put }): Generator<any, void, any> {
      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchCurrentPlan);
        console.log('response', response);
        yield put({
          type: 'setCreditsData',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching credits data:', error);
        message.error('Failed to load subscription information');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *updateProfile({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(updateUserProfile, payload);
        message.success('Profile updated successfully');

        // Refresh user data
        yield put({ type: 'fetchUserData' });
      } catch (error) {
        console.error('Error updating profile:', error);
        message.error('Failed to update profile');
      }
    },
    *changePassword({ payload }, { call }): Generator<any, void, any> {
      try {
        yield call(changePassword, payload);
        message.success('Password updated successfully');
      } catch (error) {
        console.log('Error changing password:', error);
        message.error('Failed to update password');
      }
    },
    *fetchExcludedMarketers(_, { call, put }): Generator<any, void, any> {
      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchExcludedMarketers);
        yield put({
          type: 'setExcludedMarketers',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching excluded marketers:', error);
        message.error('Failed to load excluded marketers');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *addExcludedMarketers({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(addExcludedMarketers, payload);
        message.success('Excluded marketers updated successfully');

        // Refresh excluded marketers
        yield put({ type: 'fetchExcludedMarketers' });
      } catch (error) {
        console.error('Error updating excluded marketers:', error);
        message.error('Failed to update excluded marketers');
      }
    },
    *removeExcludedMarketers({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(removeExcludedMarketers, payload);
        message.success('Excluded marketers removed successfully');

        yield put({ type: 'fetchExcludedMarketers' });
      } catch (error) {
        console.error('Error removing excluded marketers:', error);
        message.error('Failed to remove excluded marketers');
      }
    },
    *fetchExcludedManufacturers(_, { call, put }): Generator<any, void, any> {
      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchExcludedManufacturers);
        yield put({
          type: 'setExcludedManufacturers',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching excluded manufacturers:', error);
        message.error('Failed to load excluded manufacturers');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *addExcludedManufacturers({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(addExcludedManufacturers, payload);
        message.success('Excluded manufacturers updated successfully');

        // Refresh excluded manufacturers
        yield put({ type: 'fetchExcludedManufacturers' });
      } catch (error) {
        console.error('Error updating excluded manufacturers:', error);
        message.error('Failed to update excluded manufacturers');
      }
    },
    *removeExcludedManufacturers({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(removeExcludedManufacturers, payload);
        message.success('Excluded manufacturers removed successfully');

        yield put({ type: 'fetchExcludedManufacturers' });
      } catch (error) {
        console.error('Error removing excluded manufacturers:', error);
        message.error('Failed to remove excluded manufacturers');
      }
    },
    *fetchMarketersOfInterest(
      { payload }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      if (!payload?.force) {
        const loaded = yield select((state: any) => state.profile.interestListsLoaded.marketers);
        if (loaded) {
          return;
        }
      }

      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchMarketersOfInterest);
        yield put({
          type: 'setMarketersOfInterest',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching marketers of interest:', error);
        message.error('Failed to load marketers of interest');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *addMarketersOfInterest({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(addMarketersOfInterest, payload);
        message.success('Marketers of interest updated successfully');
        yield put({ type: 'fetchMarketersOfInterest', payload: { force: true } });
      } catch (error) {
        console.error('Error updating marketers of interest:', error);
        message.error('Failed to update marketers of interest');
      }
    },
    *removeMarketerOfInterest({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(removeMarketerOfInterest, payload);
        message.success('Marketer removed from interest list');
        yield put({ type: 'fetchMarketersOfInterest', payload: { force: true } });
      } catch (error) {
        console.error('Error removing marketer of interest:', error);
        message.error('Failed to remove marketer of interest');
      }
    },
    *clearMarketersOfInterest(_, { call, put }): Generator<any, void, any> {
      try {
        yield call(clearMarketersOfInterest);
        yield put({ type: 'setMarketersOfInterest', payload: [] });
        message.success('All marketers of interest cleared');
      } catch (error) {
        console.error('Error clearing marketers of interest:', error);
        message.error('Failed to clear marketers of interest');
      }
    },
    *fetchManufacturersOfInterest(
      { payload }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      if (!payload?.force) {
        const loaded = yield select(
          (state: any) => state.profile.interestListsLoaded.manufacturers,
        );
        if (loaded) {
          return;
        }
      }

      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchManufacturersOfInterest);
        yield put({
          type: 'setManufacturersOfInterest',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching manufacturers of interest:', error);
        message.error('Failed to load manufacturers of interest');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *addManufacturersOfInterest({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(addManufacturersOfInterest, payload);
        message.success('Manufacturers of interest updated successfully');
        yield put({ type: 'fetchManufacturersOfInterest', payload: { force: true } });
      } catch (error) {
        console.error('Error updating manufacturers of interest:', error);
        message.error('Failed to update manufacturers of interest');
      }
    },
    *removeManufacturerOfInterest({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(removeManufacturerOfInterest, payload);
        message.success('Manufacturer removed from interest list');
        yield put({ type: 'fetchManufacturersOfInterest', payload: { force: true } });
      } catch (error) {
        console.error('Error removing manufacturer of interest:', error);
        message.error('Failed to remove manufacturer of interest');
      }
    },
    *clearManufacturersOfInterest(_, { call, put }): Generator<any, void, any> {
      try {
        yield call(clearManufacturersOfInterest);
        yield put({ type: 'setManufacturersOfInterest', payload: [] });
        message.success('All manufacturers of interest cleared');
      } catch (error) {
        console.error('Error clearing manufacturers of interest:', error);
        message.error('Failed to clear manufacturers of interest');
      }
    },
    *fetchBrandsOfInterest(
      { payload }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      if (!payload?.force) {
        const loaded = yield select((state: any) => state.profile.interestListsLoaded.brands);
        if (loaded) {
          return;
        }
      }

      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchBrandsOfInterest);
        yield put({
          type: 'setBrandsOfInterest',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching brands of interest:', error);
        message.error('Failed to load brands of interest');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *addBrandsOfInterest({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(addBrandsOfInterest, payload);
        message.success('Brands of interest updated successfully');
        yield put({ type: 'fetchBrandsOfInterest', payload: { force: true } });
      } catch (error) {
        console.error('Error updating brands of interest:', error);
        message.error('Failed to update brands of interest');
      }
    },
    *removeBrandOfInterest({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(removeBrandOfInterest, payload);
        message.success('Brand removed from interest list');
        yield put({ type: 'fetchBrandsOfInterest', payload: { force: true } });
      } catch (error) {
        console.error('Error removing brand of interest:', error);
        message.error('Failed to remove brand of interest');
      }
    },
    *clearBrandsOfInterest(_, { call, put }): Generator<any, void, any> {
      try {
        yield call(clearBrandsOfInterest);
        yield put({ type: 'setBrandsOfInterest', payload: [] });
        message.success('All brands of interest cleared');
      } catch (error) {
        console.error('Error clearing brands of interest:', error);
        message.error('Failed to clear brands of interest');
      }
    },
    *fetchIngredientsOfInterest(
      { payload }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      if (!payload?.force) {
        const loaded = yield select((state: any) => state.profile.interestListsLoaded.ingredients);
        if (loaded) {
          return;
        }
      }

      try {
        const response = yield call(fetchIngredientsOfInterest);
        yield put({
          type: 'setIngredientsOfInterest',
          payload: getIngredientsFromResponse(response),
        });
      } catch (error) {
        console.error('Error fetching ingredients of interest:', error);
        message.error('Failed to load ingredients of interest');
      }
    },
    *fetchIngredientInterestGroups(_, { call, put }): Generator<any, void, any> {
      try {
        const response = yield call(fetchIngredientInterestGroups);
        yield put({
          type: 'setIngredientInterestGroups',
          payload: Array.isArray(response?.groups) ? response.groups : [],
        });
      } catch (error) {
        console.error('Error fetching ingredient interest groups:', error);
        yield put({ type: 'setIngredientInterestGroups', payload: [] });
      }
    },
    *createIngredientInterestGroup({ payload }, { call, put }): Generator<any, boolean, any> {
      try {
        yield call(createIngredientInterestGroup, payload);
        yield put({ type: 'fetchIngredientInterestGroups' });
        message.success('Ingredient group created');
        return true;
      } catch (error: any) {
        console.error('Error creating ingredient interest group:', error);
        message.error(
          error?.data?.message ||
            error?.response?.data?.message ||
            'Failed to create ingredient group',
        );
        return false;
      }
    },
    *updateIngredientInterestGroup({ payload }, { call, put }): Generator<any, boolean, any> {
      try {
        yield call(updateIngredientInterestGroup, payload.groupId, payload.data);
        yield put({ type: 'fetchIngredientInterestGroups' });
        message.success('Ingredient group updated');
        return true;
      } catch (error: any) {
        console.error('Error updating ingredient interest group:', error);
        message.error(
          error?.data?.message ||
            error?.response?.data?.message ||
            'Failed to update ingredient group',
        );
        return false;
      }
    },
    *deleteIngredientInterestGroup({ payload }, { call, put }): Generator<any, boolean, any> {
      try {
        yield call(deleteIngredientInterestGroup, payload.groupId);
        yield put({ type: 'fetchIngredientInterestGroups' });
        message.success('Ingredient group deleted');
        return true;
      } catch (error: any) {
        console.error('Error deleting ingredient interest group:', error);
        message.error(
          error?.data?.message ||
            error?.response?.data?.message ||
            'Failed to delete ingredient group',
        );
        return false;
      }
    },
    *updateIngredientsOfInterest({ payload }, { call, put }): Generator<any, void, any> {
      try {
        const response = yield call(updateIngredientsOfInterest, payload);
        yield put({
          type: 'setIngredientsOfInterest',
          payload: getIngredientsFromResponse(response),
        });
        message.success('Ingredients of interest saved successfully');
      } catch (error) {
        console.error('Error updating ingredients of interest:', error);
        message.error('Failed to update ingredients of interest');
      }
    },
    *removeIngredientsOfInterest({ payload }, { call, put }): Generator<any, void, any> {
      try {
        const response = yield call(removeIngredientsOfInterest, payload);
        yield put({
          type: 'setIngredientsOfInterest',
          payload: getIngredientsFromResponse(response),
        });
        message.success('Ingredient removed successfully');
      } catch (error) {
        console.error('Error removing ingredient of interest:', error);
        message.error('Failed to remove ingredient from your account');
      }
    },
    *clearIngredientsOfInterest(_, { call, put }): Generator<any, void, any> {
      try {
        const response = yield call(removeIngredientsOfInterest, { clearAll: true });
        yield put({
          type: 'setIngredientsOfInterest',
          payload: getIngredientsFromResponse(response),
        });
        message.success('All ingredients of interest cleared');
      } catch (error) {
        console.error('Error clearing ingredients of interest:', error);
        message.error('Failed to clear ingredients of interest');
      }
    },
    *fetchIngredientsToReplace(
      { payload }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      if (!payload?.force) {
        const loaded = yield select(
          (state: any) => state.profile.interestListsLoaded.replacementIngredients,
        );
        if (loaded) {
          return;
        }
      }

      try {
        const response = yield call(fetchIngredientsToReplace);
        yield put({
          type: 'setIngredientsToReplace',
          payload: getIngredientsFromResponse(response),
        });
      } catch (error) {
        console.error('Error fetching ingredients to replace:', error);
        message.error('Failed to load ingredients to replace');
      }
    },
    *updateIngredientsToReplace({ payload }, { call, put }): Generator<any, void, any> {
      try {
        const response = yield call(updateIngredientsToReplace, payload);
        yield put({
          type: 'setIngredientsToReplace',
          payload: getIngredientsFromResponse(response),
        });
        message.success('Ingredients to replace saved successfully');
      } catch (error) {
        console.error('Error updating ingredients to replace:', error);
        message.error('Failed to update ingredients to replace');
      }
    },
    *removeIngredientsToReplace({ payload }, { call, put }): Generator<any, void, any> {
      try {
        const response = yield call(removeIngredientsToReplace, payload);
        yield put({
          type: 'setIngredientsToReplace',
          payload: getIngredientsFromResponse(response),
        });
        message.success('Replacement ingredient removed successfully');
      } catch (error) {
        console.error('Error removing ingredient to replace:', error);
        message.error('Failed to remove replacement ingredient from your account');
      }
    },
    *clearIngredientsToReplace(_, { call, put }): Generator<any, void, any> {
      try {
        const response = yield call(removeIngredientsToReplace, { clearAll: true });
        yield put({
          type: 'setIngredientsToReplace',
          payload: getIngredientsFromResponse(response),
        });
        message.success('All ingredients to replace cleared');
      } catch (error) {
        console.error('Error clearing ingredients to replace:', error);
        message.error('Failed to clear ingredients to replace');
      }
    },
    *fetchPortfolioScopeInterests(
      { payload }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      if (!payload?.force) {
        const loaded = yield select(
          (state: any) => state.profile.interestListsLoaded.portfolioScope,
        );
        if (loaded) {
          return;
        }
      }

      try {
        const response = yield call(fetchPortfolioScopeInterests);
        yield put({
          type: 'setPortfolioScopeInterests',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching portfolio scope interests:', error);
        message.error('Failed to load portfolio scope interests');
      }
    },
    *updatePortfolioScopeInterests({ payload }, { call, put }): Generator<any, void, any> {
      try {
        const response = yield call(updatePortfolioScopeInterests, payload);
        yield put({
          type: 'setPortfolioScopeInterests',
          payload: response,
        });
        message.success('Portfolio scope interests updated successfully');
      } catch (error) {
        console.error('Error updating portfolio scope interests:', error);
        message.error('Failed to update portfolio scope interests');
      }
    },
    *fetchPortfolioProductInterests(
      { payload }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      if (!payload?.force) {
        const loaded = yield select(
          (state: any) => state.profile.interestListsLoaded.productAttributes,
        );
        if (loaded) {
          return;
        }
      }

      try {
        const response = yield call(fetchPortfolioProductInterests);
        yield put({
          type: 'setPortfolioProductInterests',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching product attribute interests:', error);
        message.error('Failed to load product attribute interests');
      }
    },
    *updatePortfolioProductInterests({ payload }, { call, put }): Generator<any, void, any> {
      try {
        const response = yield call(updatePortfolioProductInterests, payload);
        yield put({
          type: 'setPortfolioProductInterests',
          payload: response,
        });
        message.success('Portfolio interests updated successfully');
      } catch (error) {
        console.error('Error updating product attribute interests:', error);
        message.error('Failed to update portfolio interests');
      }
    },
  },
  reducers: {
    setUserData(state, action: { payload: UserData | null }) {
      return {
        ...state,
        userData: action.payload,
      };
    },
    setCreditsData(state, action: { payload: CreditsData | null }) {
      return {
        ...state,
        creditsData: action.payload,
      };
    },
    setExcludedMarketers(state, action: { payload: MarketerData[] }) {
      return {
        ...state,
        excludedMarketers: action.payload,
      };
    },
    setExcludedManufacturers(state, action: { payload: ManufacturerData[] }) {
      return {
        ...state,
        excludedManufacturers: action.payload,
      };
    },
    setMarketersOfInterest(state, action: { payload: MarketerData[] }) {
      return {
        ...state,
        marketersOfInterest: action.payload,
        interestListsLoaded: {
          ...state.interestListsLoaded,
          marketers: true,
        },
      };
    },
    setManufacturersOfInterest(state, action: { payload: ManufacturerData[] }) {
      return {
        ...state,
        manufacturersOfInterest: action.payload,
        interestListsLoaded: {
          ...state.interestListsLoaded,
          manufacturers: true,
        },
      };
    },
    setBrandsOfInterest(state, action: { payload: BrandData[] }) {
      return {
        ...state,
        brandsOfInterest: action.payload,
        interestListsLoaded: {
          ...state.interestListsLoaded,
          brands: true,
        },
      };
    },
    setIngredientsOfInterest(state, action: { payload: IngredientData[] }) {
      return {
        ...state,
        ingredientsOfInterest: Array.isArray(action.payload) ? action.payload : [],
        interestListsLoaded: {
          ...state.interestListsLoaded,
          ingredients: true,
        },
      };
    },
    setIngredientInterestGroups(state, action: { payload: IngredientInterestGroup[] }) {
      return {
        ...state,
        ingredientInterestGroups: Array.isArray(action.payload) ? action.payload : [],
        interestListsLoaded: {
          ...state.interestListsLoaded,
          ingredientGroups: true,
        },
      };
    },
    setIngredientsToReplace(state, action: { payload: IngredientData[] }) {
      return {
        ...state,
        ingredientsToReplace: Array.isArray(action.payload) ? action.payload : [],
        interestListsLoaded: {
          ...state.interestListsLoaded,
          replacementIngredients: true,
        },
      };
    },
    setPortfolioScopeInterests(state, action: { payload: PortfolioScopeInterestsData }) {
      return {
        ...state,
        portfolioScopeInterests: {
          applications: Array.isArray(action.payload?.applications)
            ? action.payload.applications
            : [],
          countries_of_origin: Array.isArray(action.payload?.countries_of_origin)
            ? action.payload.countries_of_origin
            : [],
          countries_of_sale: Array.isArray(action.payload?.countries_of_sale)
            ? action.payload.countries_of_sale
            : [],
          access_constraints:
            action.payload?.access_constraints &&
            typeof action.payload.access_constraints === 'object'
              ? action.payload.access_constraints
              : {},
        },
        interestListsLoaded: {
          ...state.interestListsLoaded,
          portfolioScope: true,
        },
      };
    },
    setPortfolioProductInterests(state, action: { payload: PortfolioProductInterestsData }) {
      return {
        ...state,
        flavoursOfInterest: Array.isArray(action.payload?.flavours) ? action.payload.flavours : [],
        fragrancesOfInterest: Array.isArray(action.payload?.fragrances)
          ? action.payload.fragrances
          : [],
        packagingTypesOfInterest: Array.isArray(action.payload?.packaging_types)
          ? action.payload.packaging_types
          : [],
        packagingMaterialsOfInterest: Array.isArray(action.payload?.packaging_materials)
          ? action.payload.packaging_materials
          : [],
        interestListsLoaded: {
          ...state.interestListsLoaded,
          productAttributes: true,
        },
      };
    },
    resetInterestLists(state) {
      return {
        ...state,
        marketersOfInterest: [],
        manufacturersOfInterest: [],
        brandsOfInterest: [],
        ingredientsOfInterest: [],
        ingredientInterestGroups: [],
        ingredientsToReplace: [],
        portfolioScopeInterests: { ...DEFAULT_PORTFOLIO_SCOPE_INTERESTS },
        flavoursOfInterest: [],
        fragrancesOfInterest: [],
        packagingTypesOfInterest: [],
        packagingMaterialsOfInterest: [],
        interestListsLoaded: { ...DEFAULT_INTEREST_LISTS_LOADED },
      };
    },
    setLoading(state, action: { payload: boolean }) {
      return {
        ...state,
        loading: action.payload,
      };
    },
  },
};

export default ProfileModel;
